"use client";

import { useTranslations } from "next-intl";
import * as React from "react";

import { CtaButton } from "@/components/ui/cta-button";
import { Link } from "@/components/ui/link";
import { FINANCING_SIGNUP_URL } from "@/lib/financing/constants";
import { cn } from "@/lib/utils";

import { useCalculatorEnvironment } from "./context";

interface MobileSummaryBarProps {
  total: number;
  /** The element whose visibility hides the bar (the summary card). */
  watchRef: React.RefObject<HTMLElement | null>;
}

/**
 * Pinned total and CTA for small screens, shown once the summary card has
 * scrolled out of view so the headline figure follows the visitor through
 * the inputs.
 */
export function MobileSummaryBar({ total, watchRef }: MobileSummaryBarProps) {
  const t = useTranslations("pages.pricing.calculator.results");
  const { format } = useCalculatorEnvironment();
  const [hidden, setHidden] = React.useState(true);

  // A scroll check rather than IntersectionObserver: on small screens the
  // summary sits below the inputs, so a quick scroll can jump it from below
  // the viewport to above it without ever intersecting, and the observer
  // would never fire. Show the bar once the card's bottom edge has passed
  // the top of the viewport.
  React.useEffect(() => {
    let frame: number | null = null;
    const check = () => {
      frame = null;
      const node = watchRef.current;
      if (!node) return;
      setHidden(node.getBoundingClientRect().bottom > 0);
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [watchRef]);

  return (
    <div
      aria-hidden={hidden}
      className={cn(
        "border-grey-100 bg-grey-0/95 fixed inset-x-0 bottom-0 z-30 border-t px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] backdrop-blur lg:hidden",
        "transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none",
        hidden
          ? "pointer-events-none translate-y-full opacity-0"
          : "translate-y-0 opacity-100"
      )}
    >
      <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col">
          <span className="text-ds-label-md text-grey-500">
            {t("totalLabel")}
          </span>
          <span className="text-ds-heading-h5 text-grey-950 tabular-nums">
            {format.money(total)}
          </span>
        </div>
        <CtaButton
          variant="pulse"
          size="default"
          render={
            <Link
              href={FINANCING_SIGNUP_URL}
              external
              tabIndex={hidden ? -1 : undefined}
            />
          }
        >
          {t("applyCta")}
        </CtaButton>
      </div>
    </div>
  );
}
